import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Visibility, Notifications } from "@mui/icons-material";
import {
    Tooltip,
    Badge,
} from "@mui/material";
import { TransportOrderListItemDto, transportOrderStateNames } from "../../../models/transport/transportOrder";
import { NotificationSource } from "../../../models/transport/notificationSource";
import { TransportOrderNotificationState } from "../../../models/transport/transportOrderNotification";
import { NotificationsDialog } from "./notificationsDialog";
import { confirmDesktopToMobileNotifications, confirmMobileToDesktopNotifications, getTransportOrderNotifications } from "../../../services/transportOrderService";
import { useAppSelector } from "../../../framework/customStore";

interface NotificationsStatusProps {
    row: TransportOrderListItemDto;
    notificationContext: "toDriver" | "fromDriver";
}

export const NotificationsStatus = (props: NotificationsStatusProps) => {
    const { t } = useTranslation();
    const { row, notificationContext } = props;
    const {
        transportOrderNotifications = [],
        orderNumber,
        vehicleName,
        id: transportOrderId,
    } = row;

    const employees = useAppSelector((state) => state.employees.employees);
    const [dialogOpen, setDialogOpen] = useState(false);

    const filteredNotifications = transportOrderNotifications
        ?.filter((notification) => {
            if (notificationContext === "toDriver") {
                return notification.source === NotificationSource.Desktop;
            } else if (notificationContext === "fromDriver") {
                return notification.source === NotificationSource.Mobile && notification.employeeId === null;
            }
            return false;
        })
        .reverse() || [];

    const hasNotifications = filteredNotifications.length > 0;
    const unconfirmedNotifications = filteredNotifications.filter(
        (c) => (c.state as TransportOrderNotificationState) < TransportOrderNotificationState.Confirmed
    );

    const unconfirmedCount = unconfirmedNotifications.length;

    const getText = () => {
        if (!hasNotifications) {
            return t(`notifications.noNotifications.${notificationContext}`);
        }

        if (notificationContext === "toDriver") {
            if (unconfirmedCount === 0) return t("notifications.toDriver.allConfirmed");
            if (unconfirmedCount === 1) return `${t("notifications.toDriver.oneUnconfirmed")}:`;
            if (unconfirmedCount > 1) return `${t("notifications.toDriver.multipleUnconfirmed", { value: unconfirmedCount })}:`;
        } else if (notificationContext === "fromDriver") {
            if (unconfirmedCount === 0) return t("notifications.fromDriver.allConfirmed");
            if (unconfirmedCount === 1) return `${t("notifications.fromDriver.oneUnconfirmed")}:`;
            if (unconfirmedCount > 1) return `${t("notifications.fromDriver.multipleUnconfirmed", { value: unconfirmedCount })}:`;
        }
    };

    const handleConfirmAll = async() => {
        try {
            if (notificationContext === "toDriver") {
                await confirmDesktopToMobileNotifications(transportOrderId);
            } else if (notificationContext === "fromDriver") {
                await confirmMobileToDesktopNotifications(transportOrderId);
            }

            const notifications = await getTransportOrderNotifications(transportOrderId);
            row.transportOrderNotifications = notifications?.data;

            setDialogOpen(false);
        } catch (error) {
            console.error("Error confirming notification!:", error);
        }
    };

    const IconComponent = notificationContext === "toDriver" ? Visibility : Notifications;
    const colorScheme = "primary";

    return (
        <>
            <Tooltip
                title={
                    <div>
                        <div>{getText()}</div>
                        {hasNotifications && unconfirmedNotifications.map((notification, index) => (
                            <div key={index} style={{ fontSize: "0.8rem", marginTop: "4px" }}>
                                {t(notification.message.toString(), {
                                    orderNro: orderNumber,
                                    vehicleName: vehicleName,
                                    state: t(transportOrderStateNames[notification?.orderState]),
                                    employeeName: employees?.find((e) => e.id === notification.employeeId)?.fullName || t("notifications.unknownUser"),
                                })}
                            </div>
                        ))}
                    </div>
                }
                componentsProps={{
                    popper: {
                        sx: {
                            "& .MuiTooltip-tooltip": {
                                maxWidth: 400,
                                whiteSpace: "pre-line",
                            }
                        }
                    }
                }}
            >
                <Badge
                    badgeContent={unconfirmedCount > 0 ? unconfirmedCount : null}
                    color={colorScheme}
                    overlap="circular"
                    sx={{
                        "& .MuiBadge-badge": {
                            backgroundColor: unconfirmedCount > 0 ? (colorScheme === "primary" ? "#f50057" : "#ff9800") : "transparent",
                            color: "white",
                            fontSize: "0.65rem",
                        },
                        cursor: hasNotifications ? "pointer" : "default"
                    }}
                    onClick={() => hasNotifications && setDialogOpen(true)}
                >
                    <IconComponent
                        fontSize="inherit"
                        color={hasNotifications ? (unconfirmedCount > 0 ? colorScheme : "disabled") : "disabled"}
                    />
                </Badge>
            </Tooltip>

            {dialogOpen && hasNotifications && (
                <NotificationsDialog
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    onConfirmAll={handleConfirmAll}
                    notificationContext={notificationContext}
                    notifications={filteredNotifications}
                    orderNumber={orderNumber}
                    vehicleName={vehicleName}
                />
            )}
        </>
    );
};
